import type { ComponentType, SVGProps } from "react";

/*
 * Owner 2026-10-21: the hero's action row opens with the security standing,
 * left of See how it works: Google's CASA (Cloud Application Security
 * Assessment, which Gmail's restricted scopes require) and the SOC 2 audit
 * under way. CASA publishes no badge artwork, so its mark is the icon set's
 * shield with the name set in type. The shield has no check: the assessment
 * has no pass on file yet (external communications policy), so neither badge
 * claims a result. Each badge says its status once.
 */
export const TRUST_CASA_NOTE = "Security assessment";
export const TRUST_SOC2_NOTE = "Audit in progress";

/** A 24px outline icon in the icon set's stroke, so these marks match the rest. */
function outlineIcon(d: string) {
  return function OutlineIcon(props: SVGProps<SVGSVGElement>) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d={d} />
      </svg>
    );
  };
}

/** The outline set's ShieldCheckIcon without its check. */
const ShieldIcon = outlineIcon(
  "M12 2.714A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z",
);

/**
 * SOC 2's mark: the outline set's CheckBadgeIcon seal with clock hands where
 * its check was, an attestation seal still under way. The AICPA's SOC logo is
 * licensed only once a CPA firm issues the report, so it can't stand in here.
 */
const AuditSealIcon = outlineIcon(
  "M12 8.25V12h2.625M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z",
);

const BADGES: ReadonlyArray<{
  name: string;
  note: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
}> = [
  { name: "CASA", note: TRUST_CASA_NOTE, Icon: ShieldIcon },
  { name: "SOC 2", note: TRUST_SOC2_NOTE, Icon: AuditSealIcon },
];

/**
 * CASA and SOC 2, side by side; static, so they take no focus or touch.
 * `className` adds to the row's layout (a phone's row wraps rather than overflow).
 */
export default function LandingTrustBadges({ t, className = "" }: { t: (source: string) => string; className?: string }) {
  return (
    <ul data-landing-trust="true" aria-label={t("Security")} className={`flex shrink-0 items-center gap-3 ${className}`}>
      {BADGES.map(({ name, note, Icon }) => (
        <li key={name} className="flex items-center gap-1.5 text-start">
          <Icon aria-hidden="true" className="size-5 shrink-0 text-cyan-200/80" />
          <span className="flex flex-col leading-none">
            <span className="text-[13px] font-semibold tracking-[0.06em] text-white/90">{name}</span>
            <span className="mt-[3px] whitespace-nowrap text-[11px] text-white/55">{t(note)}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}
